import React, { useState } from 'react';
import { Loader2, Radio } from 'lucide-react';
import useLiveRigFeeds from '@/hooks/useLiveRigFeeds';
import RigTelemetryCard from '@/components/telemetry/RigTelemetryCard';
import FeaturedRigCard from '@/components/telemetry/FeaturedRigCard';
import RigDetailModal from '@/components/telemetry/RigDetailModal';
import VenueDisplayHeader from '@/components/venue/VenueDisplayHeader';

/**
 * Wall-mounted live telemetry display.
 *
 * Public and chrome-free: it renders outside the site layout, and carries no
 * outbound links at all, so nothing on screen can navigate away from the wall.
 * Tapping a rig opens its detail in place, over the board, instead of leaving
 * the venue screen for the website.
 */
export default function VenueLive() {
  const { rigs, loading, featured, others, liveCount } = useLiveRigFeeds();
  // Keyed by rig_key so the popup keeps following the same rig as its live
  // packets land, rather than holding a stale snapshot of it.
  const [openKey, setOpenKey] = useState(null);
  const openRig = openKey ? rigs.find((r) => r.rig_key === openKey) : null;

  return (
    <div className="min-h-screen bg-[#0d0d0f] text-white flex flex-col">
      <VenueDisplayHeader
        title="Live Rig Telemetry"
        subtitle={`${liveCount} of ${rigs.length} rigs broadcasting`}
      />

      <main className="flex-1 px-6 lg:px-10 py-8 space-y-10">
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <section>
                <SectionTitle kicker="MAIN FEEDS" title="Featured Rigs" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {featured.map((rig) => (
                    <FeaturedRigCard key={rig.id} rig={rig} onOpen={setOpenKey} />
                  ))}
                </div>
              </section>
            )}

            <section>
              <SectionTitle kicker="ALL RIGS" title="Live Grid" />
              {others.length === 0 ? (
                <div className="text-center py-16 border border-white/5 bg-[#111115]">
                  <Radio className="w-7 h-7 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No rigs are broadcasting yet.</p>
                  <p className="text-white/25 text-xs mt-1">Start the telemetry pusher on each rig PC to bring it online.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {others.map((rig) => (
                    <RigTelemetryCard key={rig.id} rig={rig} onOpen={setOpenKey} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <RigDetailModal rig={openRig} onClose={() => setOpenKey(null)} />
    </div>
  );
}

function SectionTitle({ kicker, title }) {
  return (
    <div className="mb-4">
      <p className="text-primary text-[10px] font-bold tracking-widest mb-1.5">{kicker}</p>
      <h2 className="text-white font-black text-lg uppercase tracking-tight">{title}</h2>
    </div>
  );
}